import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useAgencies from '@hooks/useAgencies'
import useBrandThemes from '@hooks/useBrandThemes'
import useProviders from '@hooks/useProviders'
import usePublications from '@hooks/usePublications'
import useServiceProducts from '@hooks/useServiceProducts'
import useTemplateGroups from '@hooks/useTemplateGroups'

import type {
  AgencyModel, BrandThemeModel, ProviderModel, PublicationModel, ServiceProductModel, TemplateGroupModel,
} from '@models/types'

import type { Permissions } from './useAssignTemplatePagePermissionsModal'

const watchEntityKeys = [
  'agencies',
  'brandThemes',
  'providers',
  'publications',
  'serviceProducts',
  'templateGroups',
]

export type FilteredEntities = {
  agencies: AgencyModel[],
  brandThemes: BrandThemeModel[],
  providers: ProviderModel[],
  publications: PublicationModel[],
  serviceProducts: ServiceProductModel[],
  templateGroups: TemplateGroupModel[],
}

type UseScopeEntitiesOptions = {
  activeStepKey: string,
  filters: {
    brand_id?: number,
    service_id?: number,
    string: string,
  },
  permissions: Permissions,
}

function useScopedEntities(options: UseScopeEntitiesOptions) {
  const {
    activeStepKey,
    filters: {
      brand_id,
      service_id,
      string,
    },
    permissions: {
      queued: {
        providerIds: queuedProviderIds,
      },
    },
  } = options

  const {
    updatedEntities: {
      agencies: agenciesUpdatedAt = 0,
      brandThemes: brandThemesUpdatedAt = 0,
      providers: providersUpdatedAt = 0,
      publications: publicationsUpdatedAt = 0,
      serviceProducts: serviceProductsUpdatedAt = 0,
      templateGroups: templateGroupsUpdatedAt = 0,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  // Entities
  // Agencies
  const agenciesPayload = useAgencies({
    filters: {
      brand_id,
      string,
    },
    performHttpRequests: !!brand_id && !string,
  })

  const { filteredAgencies } = agenciesPayload

  // Brand Themes
  const brandThemesPayload = useBrandThemes({
    filters: {
      brand_id,
      string,
    },
    performHttpRequests: activeStepKey === 'brandThemeIds' && !!brand_id && !string,
  })

  const { filteredBrandThemes } = brandThemesPayload

  // Providers
  const providersPayload = useProviders({
    filters: {
      assigned_brand_id: brand_id,
      string,
    },
    performHttpRequests: !!brand_id && !string,
    requestOptions: {
      include_provider_assigned_brand_ids: true,
    },
  })

  const { filteredProviders } = providersPayload
  const hasQueuedProviders = !!queuedProviderIds.length

  // Publications
  const publicationsPayload = usePublications({
    filters: {
      provider_ids: queuedProviderIds,
      string,
    },
    performHttpRequests: activeStepKey === 'publicationIds' && hasQueuedProviders && !string,
    requestOptions: {},
  })

  const { filteredPublications } = publicationsPayload

  // Service Products
  const serviceProductsPayload = useServiceProducts({
    filters: {
      provider_ids: queuedProviderIds,
      service_id,
      string,
    },
    performHttpRequests: hasQueuedProviders && !!service_id && !string,
    requestOptions: {
      include_service_product_provider_ids: true,
    },
  })

  const { filteredServiceProducts } = serviceProductsPayload

  // Template Groups
  const templateGroupsPayload = useTemplateGroups({
    filters: {
      brand_id,
      string,
    },
    performHttpRequests: activeStepKey === 'templateGroupIds' && !!brand_id && !string,
  })

  const { filteredTemplateGroups } = templateGroupsPayload

  // Cache Key
  // @ts-expect-error TS2365
  const entitiesUpdatedAt = agenciesUpdatedAt
    + brandThemesUpdatedAt
    + providersUpdatedAt
    + publicationsUpdatedAt
    + serviceProductsUpdatedAt
    + templateGroupsUpdatedAt

  return {
    entitiesUpdatedAt,
    filteredEntities: {
      agencies: filteredAgencies,
      brandThemes: filteredBrandThemes,
      providers: filteredProviders,
      publications: filteredPublications,
      serviceProducts: filteredServiceProducts,
      templateGroups: filteredTemplateGroups,
    },
    payloads: {
      agencies: agenciesPayload,
      brandThemes: brandThemesPayload,
      providers: providersPayload,
      publications: publicationsPayload,
      serviceProducts: serviceProductsPayload,
      templateGroups: templateGroupsPayload,
    },
  }
}

export default useScopedEntities
